import createClient from "openapi-fetch";
import React, { createContext, useContext, useMemo } from "react";
import type { paths } from "../api/types";
import { API_ENDPOINT, DEFAULT_CACHE_TTL_SECONDS } from "../config";
import { useAuth } from "./AuthContext";
import Caching from "../CacheManager";
import { useAppContext } from "./AppContext";
import { useError } from "./ErrorContext";
import { Translate } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const AUTHETNICATION_TIMEOUT_CODE = 419;
interface ApiContextType {
  client: ReturnType<typeof createClient<paths>>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, logout } = useAuth();
  const { setSessionExpired } = useAppContext();
  const { showError } = useError();

  const client = useMemo(() => {
    return createClient<paths>({
      baseUrl: API_ENDPOINT.replace(/\/+$/, ""),
      fetch: async (
        input: Request & { cacheable?: boolean; cacheTTL?: number },
        init?: RequestInit
      ) => {
        const cacheKey = input.url.replace(API_ENDPOINT, "");
        const cacheTTL = input.cacheTTL ?? DEFAULT_CACHE_TTL_SECONDS;

        const headers = new Headers(input?.headers || {});
        if (user) {
          const token = await user.getIdToken();
          headers.set("Authorization", `Bearer ${token}`);
        }

        if (input.method === "GET" && input.cacheable) {
          const entry = Caching.get(cacheKey);
          if (entry) {
            return new Response(new Blob([JSON.stringify(entry)]), {
              headers: { "Content-Type": "application/json" },
            });
          }
        }
        if (input.method === "POST") {
          headers.set("Cache-Control", "no-store");
          headers.set("Pragma", "no-cache");
        }

        try {
          const response = await fetch(input, { ...init, headers });

          if (user && response.status === AUTHETNICATION_TIMEOUT_CODE) {
            setSessionExpired(true);
            logout();
            return response;
          }

          const responseJson = await response.clone().json();
          if (response.ok && input?.cacheable) {
            Caching.set(cacheKey, responseJson, cacheTTL);
          }
          return response;
        } catch (e) {
          throw e;
        }
      },
    });
  }, [user]);

  const value = { client };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};
